import { Anchor, Badge, Button, Group, Progress, Table, Text } from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';

import { useMessages } from '../api/hooks';
import { DataState } from '../components/DataState';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { CHANNEL_LABEL, fmtNumber, fmtRelative } from '../theme/format';

export function MessagesPage() {
  const { data, isLoading, error } = useMessages();
  const navigate = useNavigate();

  return (
    <>
      <PageHeader
        title="Messages"
        description="Every send, with per-recipient delivery status behind each one."
        action={
          <Button leftSection={<IconSend size={16} />} onClick={() => navigate('/compose')}>
            New send
          </Button>
        }
      />

      <DataState
        loading={isLoading}
        error={error}
        empty={!data?.length}
        emptyTitle="No messages yet"
        emptyDescription="Sends made through the API or from this dashboard appear here."
        action={<Button onClick={() => navigate('/compose')}>Send one</Button>}
      >
        <Table.ScrollContainer minWidth={860}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Created</Table.Th>
                <Table.Th>Channel</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Progress</Table.Th>
                <Table.Th ta="right">Recipients</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data?.map((m) => {
                const done = m.sent + m.failed + m.skipped;
                return (
                  <Table.Tr key={m.id} style={{ cursor: 'pointer' }}>
                    <Table.Td>
                      <Anchor component={Link} to={`/messages/${m.id}`} size="sm">
                        {fmtRelative(m.created_at)}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light">{CHANNEL_LABEL[m.channel] ?? m.channel}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{m.source}</Text>
                    </Table.Td>
                    <Table.Td><StatusBadge status={m.status} /></Table.Td>
                    <Table.Td w={200}>
                      {m.total > 0 ? (
                        <Progress.Root size="sm">
                          <Progress.Section value={(m.sent / m.total) * 100} color="blue" />
                          <Progress.Section value={(m.failed / m.total) * 100} color="red" />
                          <Progress.Section value={(m.skipped / m.total) * 100} color="gray" />
                        </Progress.Root>
                      ) : (
                        <Text size="xs" c="dimmed">—</Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Group gap={4} justify="flex-end">
                        <Text size="sm">{fmtNumber(done)}</Text>
                        <Text size="sm" c="dimmed">/ {fmtNumber(m.total)}</Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </DataState>
    </>
  );
}
